import { Component, computed, signal, OnInit, inject, effect, input, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { ComponentCanDeactivate } from '../../../../../core/guards/unsaved-changes.guard';

// PrimeNG Components
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';

// Services
import { ProfessionalsService } from '../../../../../core/services/professionals.service';
import { ProfessionalsDataService, TipologiaFiduciario } from '../../../../services/professionals-data.service';
import { ProfessionalsWizardService } from '../../../../services/professionals-wizard.service';
import { UserSessionService } from '../../../../../core/services/mock-auth.service';
import { FormDropdownService } from '../../../../../core/services/form-dropdown.service';
import { GeographyService } from '../../../../../core/services/geography.service';
import { ConfirmationDialogService } from '../../../../../core/services/confirmation-dialog.service';
import { hasMeaningfulText, normalizeTrimmedText } from '../../../../../core/validators/text-value.utils';

// Models


interface SubResponsible {
  id?: number;
  companyName: string;
  headquartersCountry: string;
  operatingRegion: string;
  operatingProvince: string;
  operatingMunicipality: string;
  dataProcessingLocation: 'EU' | 'EXTRA_EU' | '';
  specificLocationLegalBasis: string;
  extraEuTransferSafeguards: string;
  dataTypesProcessed: string[];
  dataTypesProcessedOther: string;
  processesDescription: string;
}

@Component({
  selector: 'app-vrf-questionnaire',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    Select,
    RadioButtonModule,
    TooltipModule,
    MultiSelectModule,
    CheckboxModule,
    Toast,
  ],
  providers: [MessageService],
  templateUrl: './vrf-questionnaire.component.html',
  styleUrls: ['./vrf-questionnaire.component.scss'],
})
export class VrfQuestionnaireComponent implements OnInit, ComponentCanDeactivate {
  // Optional input for professional ID (used in review mode)
  professionalIdInput = input<string | null>(null);
  private readonly hasMeaningfulText = hasMeaningfulText;
  private readonly normalizeTrimmedText = normalizeTrimmedText;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private professionalsService = inject(ProfessionalsService);
  private professionalsDataService = inject(ProfessionalsDataService);
  private professionalsWizardService = inject(ProfessionalsWizardService);
  private mockAuthService = inject(UserSessionService);
  private messageService = inject(MessageService);
  private formDropdownService = inject(FormDropdownService);
  private geographyService = inject(GeographyService);
  private confirmDialogService = inject(ConfirmationDialogService);

  /**
   * Get current professional ID from input or route param or mockAuthService
   */
  private getCurrentProfessionalId(): string | null {
    // Priority 1: Use professionalIdInput if provided (from parent component)
    const inputId = this.professionalIdInput();
    if (inputId) {
      return inputId;
    }
    
    // Priority 2: Use route parameter if provided (when navigating with ID)
    const routeId = this.route.snapshot.paramMap.get('id');
    if (routeId) {
      return routeId;
    }
    
    // Priority 3: Use session storage (normal professional flow)
    return this.mockAuthService.getDemoProfessionalId();
  }

  // Track unsaved changes
  private isDirty = signal<boolean>(false);
  private isInitialLoad = signal<boolean>(true);

  // Live region for accessibility
  liveRegionMessage = signal<string>('');

  // Touch tracking for validation
  private touchedFields = new Set<string>();

  // Invalid fields tracking for visual error feedback
  invalidFields = signal<Set<string>>(new Set());
  validationErrors = signal<Map<string, string>>(new Map());

  // SECTION 1: RESPONSABILE DEL TRATTAMENTO
  responsibleCompanyName = signal<string>('');
  dpoFirstName = signal<string>('');
  dpoLastName = signal<string>('');
  dpoEmail = signal<string>('');

  // SECTION 2: DATA RECIPIENTS CATEGORIES
  dataRecipientsCategories = signal<string[]>([]);
  dataRecipientsCategoriesOther = signal<string>('');
  dataRecipientsCategoriesOptions = signal<{ label: string; value: string }[]>([]);
  dataTransferOutsideEu = signal<boolean | null>(null);
  gdprComplianceMeasures = signal<boolean | null>(null);

  // SECTION 3: RETENTION AND DELETION
  dataDeletionMethod = signal<string>('');
  dataDeletionMethodOptions = signal<{ label: string; value: string }[]>([]);

  // SECTION 4: IT SYSTEMS USED
  itSystemsUsed = signal<string>('SOL');
  itSystemsOther = signal<string>('');
  itSystemsUsedOptions = signal<{ label: string; value: string }[]>([]);

  // SECTION 5: SECURITY MEASURES
  credentialsAccess = signal<boolean | null>(null);
  softwareUpdates = signal<boolean | null>(null);
  passwordUpdates = signal<boolean | null>(null);
  antivirusFirewall = signal<boolean | null>(null);
  periodicBackup = signal<boolean | null>(null);
  staffTraining = signal<boolean | null>(null);
  authorizedPersonnelList = signal<boolean | null>(null);

  // SECTION 6: SUB-RESPONSIBLES
  hasSubResponsibles = signal<boolean>(false);
  subResponsibles = signal<SubResponsible[]>([]);

  // VRF STATUS
  vrfStatus = signal<'DRAFT' | 'FILLED' | null>(null);
  
  // Computed: verify if VRF is completed
  isVRFCompleted = computed(() => this.vrfStatus() === 'FILLED');

  // Computed: verifica se siamo in modalità read-only
  isReadOnly = computed(() => {
    return this.professionalsDataService.isReadOnly();
  });

  countriesOptions = signal<{ label: string; value: string }[]>([]);

  // Geography options per sub-responsible (key: sub-responsible id)
  subResponsibleProvincesOptions = signal<Map<number, { label: string; value: string }[]>>(new Map());
  subResponsibleRegionsOptions = signal<Map<number, { label: string; value: string }[]>>(new Map());
  subResponsibleMunicipalitiesOptions = signal<Map<number, { label: string; value: string }[]>>(new Map());

  streetTypeOptions = signal<{ label: string; value: string }[]>([]);

  dataProcessingLocationOptions = signal<{ label: string; value: string }[]>([]);

  dataTypesOptions = signal<{ label: string; value: string }[]>([]);

  // SECTION 7: SYSTEM ADMINISTRATORS
  systemAdministratorsInfo = signal<string>('');

  // SECTION 8: CLOUD
  cloudStorageUsed = signal<boolean>(false);
  cloudType = signal<string>('');

  // Loading state
  isLoading = signal<boolean>(false);
  isSaving = signal<boolean>(false);

  constructor() {
    effect(() => {
      const generalData = this.professionalsWizardService.cachedGeneralData();
      const businessName = this.getGeneralDataBusinessName(generalData);
      const dpoFirstName = this.getGeneralDataDpoName(generalData, 'firstname');
      const dpoLastName = this.getGeneralDataDpoName(generalData, 'lastname');

      if (businessName && !untracked(() => this.hasMeaningfulText(this.responsibleCompanyName()))) {
        this.responsibleCompanyName.set(businessName);
      }
      if (dpoFirstName && !untracked(() => this.hasMeaningfulText(this.dpoFirstName()))) {
        this.dpoFirstName.set(dpoFirstName);
      }
      if (dpoLastName && !untracked(() => this.hasMeaningfulText(this.dpoLastName()))) {
        this.dpoLastName.set(dpoLastName);
      }
    });

    // Track form changes to detect unsaved modifications
    // Skip tracking during initial load to avoid false positives
    effect(() => {
      if (this.isInitialLoad()) return;

      // Watch all form fields for changes
      this.responsibleCompanyName();
      this.dpoFirstName();
      this.dpoLastName();
      this.dpoEmail();
      this.dataRecipientsCategories();
      this.dataRecipientsCategoriesOther();
      this.dataTransferOutsideEu();
      this.gdprComplianceMeasures();
      this.dataDeletionMethod();
      this.itSystemsUsed();
      this.itSystemsOther();
      this.credentialsAccess();
      this.softwareUpdates();
      this.passwordUpdates();
      this.antivirusFirewall();
      this.periodicBackup();
      this.staffTraining();
      this.authorizedPersonnelList();
      this.hasSubResponsibles();
      this.subResponsibles();
      this.systemAdministratorsInfo();
      this.cloudStorageUsed();
      this.cloudType();

      // Mark form as dirty when any field changes
      this.isDirty.set(true);
    });
  }

  /**
   * Implements ComponentCanDeactivate interface
   * Called by UnsavedChangesGuard before navigation
   */
  canDeactivate(): Observable<boolean> | boolean {
    if (this.professionalsWizardService.consumeNavigationWithoutWarning()) {
      return true;
    }

    // Allow navigation if in read-only mode (no editing possible)
    if (this.isReadOnly()) {
      return true;
    }
    
    // Allow navigation if no unsaved changes
    if (!this.isDirty()) {
      return true;
    }

    // Show confirmation dialog using centralized service
    return new Observable<boolean>((observer) => {
      this.confirmDialogService.confirmCancelEdit(
        () => {
          // User confirmed, reset dirty flag and allow navigation
          this.isDirty.set(false);
          observer.next(true);
          observer.complete();
        },
        () => {
          // User cancelled, block navigation
          observer.next(false);
          observer.complete();
        }
      );
    });
  }

  ngOnInit(): void {
    // VRF can be accessed directly via route or as embedded component
    // So we need to ensure read-only state is loaded
    const professionalId = this.getCurrentProfessionalId();
    if (professionalId) {
      // Load current registration step from backend (needed for read-only mode detection)
      this.professionalsWizardService.loadCurrentStep(professionalId);
      
      // Load completion status for navbar steps (when accessed directly)
      console.log('🔍 [VRF] Loading completion status for navbar...');
      this.professionalsWizardService.preloadGeneralDataStatus(professionalId);
      
      // Ensure tipologiaFiduciario is set — fallback to wizard cache if service has no value
      // (happens when the user navigates directly to VRF without going through general/specific data steps)
      let tipologia = this.professionalsDataService.getTipologiaFiduciario();
      if (!tipologia) {
        const cachedData = this.professionalsWizardService.cachedGeneralData();
        if (cachedData?.professionalType) {
          const cachedTipologia = cachedData.professionalType as TipologiaFiduciario;
          const cachedCarrozzeria = cachedData.professionalSubType || null;
          this.professionalsDataService.initializeProfessionalsData(cachedTipologia, cachedCarrozzeria ?? undefined);
          tipologia = cachedTipologia;
          console.log('✅ [VRF] Tipologia loaded from wizard cache:', cachedTipologia);
        } else {
          console.warn('⚠️ [VRF] TipologiaFiduciario not available — precompilation will be skipped');
        }
      }

      if (tipologia) {
        this.professionalsWizardService.preloadSpecificDataStatus(professionalId, tipologia);
      }
      
      this.professionalsWizardService.preloadQuestionnaireStatus(professionalId);
    }
    
    // Set this step as active in wizard
    this.professionalsWizardService.setStep(2);
    
    this.loadDropdownOptions();
    this.loadVRFData();
  }

  private getGeneralDataBusinessName(generalData: any): string {
    return generalData?.companyData?.businessName || generalData?.businessName || '';
  }

  private getGeneralDataDpoName(generalData: any, fieldName: 'firstname' | 'lastname'): string {
    return generalData?.legalRepresentative?.[fieldName]
      || generalData?.[fieldName]
      || generalData?.companyData?.[fieldName]
      || '';
  }

  /**
   * Load dropdown options from FormDropdownService and GeographyService
   */
  private loadDropdownOptions(): void {
    // Load CANCELLATION_METHOD from FormDropdownService
    this.formDropdownService.getFormDropdownValues('VRF_QUESTIONNAIRE', 'CANCELLATION_METHOD').subscribe({
      next: (response) => {
        if (response.data) {
          this.dataDeletionMethodOptions.set(
            response.data.map(item => ({ label: item.valueDescription, value: item.valueKey }))
          );
        }
      },
      error: (err) => console.error('❌ Error loading CANCELLATION_METHOD:', err)
    });

    // Load IT_PROGRAMS from FormDropdownService
    this.formDropdownService.getFormDropdownValues('VRF_QUESTIONNAIRE', 'IT_PROGRAMS').subscribe({
      next: (response) => {
        if (response.data) {
          this.itSystemsUsedOptions.set(
            response.data.map(item => ({ label: item.valueDescription, value: item.valueKey }))
          );
        }
      },
      error: (err) => console.error('❌ Error loading IT_PROGRAMS:', err)
    });

    // Load PROCESSING_LOCATION from FormDropdownService
    this.formDropdownService.getFormDropdownValues('VRF_QUESTIONNAIRE', 'PROCESSING_LOCATION').subscribe({
      next: (response) => {
        if (response.data) {
          this.dataProcessingLocationOptions.set(
            response.data.map(item => ({ label: item.valueDescription, value: item.valueKey }))
          );
        }
      },
      error: (err) => console.error('❌ Error loading PROCESSING_LOCATION:', err)
    });

    // Load DATA_TYPE from FormDropdownService
    this.formDropdownService.getFormDropdownValues('VRF_QUESTIONNAIRE', 'DATA_TYPE').subscribe({
      next: (response) => {
        if (response.data) {
          this.dataTypesOptions.set(
            response.data.map(item => ({ label: item.valueDescription, value: item.valueKey }))
          );
        }
      },
      error: (err) => console.error('❌ Error loading DATA_TYPE:', err)
    });

    // Use the same recipient categories shown in the Atto di Nomina.
    this.formDropdownService.getFormDropdownValues('ATTO_DI_NOMINA', 'RECIPIENT_CATEGORY').subscribe({
      next: (response) => {
        if (response.data) {
          this.dataRecipientsCategoriesOptions.set(
            response.data.map(item => ({ label: item.valueDescription, value: item.valueKey }))
          );
          this.dataRecipientsCategories.update((values) => this.normalizeRecipientCategoryKeys(values));
        }
      },
      error: (err) => console.error('❌ Error loading RECIPIENT_CATEGORY:', err)
    });

    // Load Countries from GeographyService
    this.geographyService.getCountries().subscribe({
      next: (countries) => {
        this.countriesOptions.set(
          countries.map(country => ({ label: country.name, value: country.code }))
        );
      },
      error: (err) => console.error('❌ Error loading countries:', err)
    });
  }

  private loadVRFData(): void {
    const professionalId = this.getCurrentProfessionalId();
    if (!professionalId) {
      console.warn('⚠️ No professional ID found');
      return;
    }

    this.isLoading.set(true);
    
    this.professionalsService.getVRFQuestionnaire(professionalId).subscribe({
      next: (response) => {
        // Populate all fields from response
        const responseCompanyName = response.responsibleCompanyName || '';
        this.responsibleCompanyName.set(
          this.hasMeaningfulText(responseCompanyName)
            ? responseCompanyName
            : this.getGeneralDataBusinessName(this.professionalsWizardService.cachedGeneralData())
        );
        const generalData = this.professionalsWizardService.cachedGeneralData();
        const responseDpoFirstName = response.dpoFirstName || '';
        const responseDpoLastName = response.dpoLastName || '';
        this.dpoFirstName.set(
          this.hasMeaningfulText(responseDpoFirstName)
            ? responseDpoFirstName
            : this.getGeneralDataDpoName(generalData, 'firstname')
        );
        this.dpoLastName.set(
          this.hasMeaningfulText(responseDpoLastName)
            ? responseDpoLastName
            : this.getGeneralDataDpoName(generalData, 'lastname')
        );
        this.dpoEmail.set(response.dpoEmail || '');
        this.dataRecipientsCategories.set(
          this.normalizeRecipientCategoryKeys(
            this.parseRecipientCategories(response.dataRecipientsCategories)
          )
        );
        this.dataRecipientsCategoriesOther.set(response.dataRecipientsCategoriesOther || '');
        this.dataTransferOutsideEu.set(response.dataTransferOutsideEu ?? null);
        this.gdprComplianceMeasures.set(response.gdprComplianceMeasures ?? null);
        this.dataDeletionMethod.set(response.dataDeletionMethod || '');
        this.itSystemsUsed.set('SOL');
        this.itSystemsOther.set(response.itSystemsOther || '');
        this.credentialsAccess.set(response.credentialsAccess ?? null);
        this.softwareUpdates.set(response.softwareUpdates ?? null);
        this.passwordUpdates.set(response.passwordUpdates ?? null);
        this.antivirusFirewall.set(response.antivirusFirewall ?? null);
        this.periodicBackup.set(response.periodicBackup ?? null);
        this.staffTraining.set(response.staffTraining ?? null);
        this.authorizedPersonnelList.set(response.authorizedPersonnelList ?? null);
        this.hasSubResponsibles.set(response.hasSubResponsibles || false);
        
        // Map sub-responsibles: BE usa headquarters*, FE usa operating* internamente
        const mappedSubResponsibles = (response.subResponsibles || []).map(sub => ({
          ...sub,
          operatingRegion: (sub as any).headquartersRegion || sub.operatingRegion || '',
          operatingProvince: (sub as any).headquartersProvince || sub.operatingProvince || '',
          operatingMunicipality: (sub as any).headquartersMunicipality || sub.operatingMunicipality || '',
          dataTypesProcessed: sub.dataTypesProcessed || [],
          dataTypesProcessedOther: (sub as any).dataTypesProcessedOther || '',
        }));
        this.subResponsibles.set(mappedSubResponsibles);
        
        // Load provinces, regions, and municipalities for existing sub-responsibles with Italy
        mappedSubResponsibles.forEach(sub => {
          if (sub.id && sub.headquartersCountry === 'IT') {
            this.loadGeographyOptionsForSubResponsible(sub.id, sub.operatingRegion, sub.operatingProvince);
          }
        });
        
        this.systemAdministratorsInfo.set(response.systemAdministratorsInfo || '');
        this.cloudStorageUsed.set(response.cloudStorageUsed || false);
        this.cloudType.set(response.cloudType || '');
        
        // Set VRF status
        this.vrfStatus.set(response.status || 'DRAFT');
        
        this.isLoading.set(false);
        
        // Reset initial load flag after all data is loaded
        // Use setTimeout to ensure all signals are updated before tracking changes
        setTimeout(() => this.isInitialLoad.set(false), 0);
      },
      error: (error) => {
        this.isLoading.set(false);
        
        // Reset initial load flag even on error (form is ready for input)
        setTimeout(() => this.isInitialLoad.set(false), 0);
        
        // Handle 404 gracefully - it's expected on first compilation (no data exists yet)
        if (error.status === 404) {
          // Pre-set SOL as default for IT systems (as per spec: "se assenti indicare solo SOL")
          this.itSystemsUsed.set('SOL');
          return;
        }
        
        // Log other errors to console only (no user-facing messages)
        console.error('Error loading VRF questionnaire:', error);
      },
    });
  }

  markFieldTouched(fieldName: string): void {
    this.touchedFields.add(fieldName);
  }

  isFieldTouched(fieldName: string): boolean {
    return this.touchedFields.has(fieldName);
  }

  addSubResponsible(): void {
    const newId = this.subResponsibles().length + 1;
    this.subResponsibles.update(list => [
      ...list,
      {
        id: newId,
        companyName: '',
        headquartersCountry: 'IT', // Preselect Italy
        operatingRegion: '',
        operatingProvince: '',
        operatingMunicipality: '',
        dataProcessingLocation: '',
        specificLocationLegalBasis: '',
        extraEuTransferSafeguards: '',
        dataTypesProcessed: [],
        dataTypesProcessedOther: '',
        processesDescription: '',
      },
    ]);
    
    // Load provinces for Italy (default country) using helper method
    this.loadGeographyOptionsForSubResponsible(newId);
    
    this.announceToScreenReader('Sub-responsabile aggiunto. Compila i campi obbligatori.');
  }

  removeSubResponsible(id: number | undefined): void {
    if (id === undefined) return;
    this.subResponsibles.update(list => list.filter(s => s.id !== id));
    this.announceToScreenReader('Sub-responsabile rimosso.');
  }

  onSubResponsibleCountryChange(id: number | undefined, countryCode: string): void {
    if (id === undefined) return;
    
    // Update sub-responsible and reset dependent fields
    this.subResponsibles.update(list =>
      list.map(s =>
        s.id === id
          ? { ...s, headquartersCountry: countryCode, operatingRegion: '', operatingProvince: '', operatingMunicipality: '' }
          : s
      )
    );

    // Clear dependent options
    this.subResponsibleRegionsOptions.update(map => {
      const newMap = new Map(map);
      newMap.delete(id);
      return newMap;
    });
    this.subResponsibleProvincesOptions.update(map => {
      const newMap = new Map(map);
      newMap.delete(id);
      return newMap;
    });
    this.subResponsibleMunicipalitiesOptions.update(map => {
      const newMap = new Map(map);
      newMap.delete(id);
      return newMap;
    });

    // Load regions only if country is Italy
    if (countryCode === 'IT') {
      this.geographyService.getRegions('IT').subscribe({
        next: (regions) => {
          this.subResponsibleRegionsOptions.update(map => {
            const newMap = new Map(map);
            newMap.set(id, regions.map(r => ({ label: r.name, value: r.code })));
            return newMap;
          });
        },
        error: (err) => {
          console.error('Error loading regions:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile caricare la lista delle regioni'
          });
        }
      });
    }
  }

  onSubResponsibleRegionChange(id: number | undefined, regionCode: string): void {
    if (id === undefined) return;
    
    // Update sub-responsible and reset dependent fields
    this.subResponsibles.update(list =>
      list.map(s =>
        s.id === id
          ? { ...s, operatingRegion: regionCode, operatingProvince: '', operatingMunicipality: '' }
          : s
      )
    );

    // Clear dependent options
    this.subResponsibleProvincesOptions.update(map => {
      const newMap = new Map(map);
      newMap.delete(id);
      return newMap;
    });
    this.subResponsibleMunicipalitiesOptions.update(map => {
      const newMap = new Map(map);
      newMap.delete(id);
      return newMap;
    });

    // Load provinces if region selected
    if (regionCode) {
      this.geographyService.getProvinces(regionCode).subscribe({
        next: (provinces) => {
          this.subResponsibleProvincesOptions.update(map => {
            const newMap = new Map(map);
            newMap.set(id, provinces.map(p => ({ label: p.name, value: p.code })));
            return newMap;
          });
        },
        error: (err) => {
          console.error('Error loading provinces:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile caricare la lista delle province'
          });
        }
      });
    }
  }

  onSubResponsibleProvinceChange(id: number | undefined, provinceCode: string): void {
    if (id === undefined) return;
    
    // Update sub-responsible and reset dependent fields
    this.subResponsibles.update(list =>
      list.map(s =>
        s.id === id
          ? { ...s, operatingProvince: provinceCode, operatingMunicipality: '' }
          : s
      )
    );

    // Clear dependent options
    this.subResponsibleMunicipalitiesOptions.update(map => {
      const newMap = new Map(map);
      newMap.delete(id);
      return newMap;
    });

    // Load municipalities if province selected
    if (provinceCode) {
      this.geographyService.getMunicipalities(provinceCode).subscribe({
        next: (municipalities) => {
          this.subResponsibleMunicipalitiesOptions.update(map => {
            const newMap = new Map(map);
            newMap.set(id, municipalities.map(m => ({ label: m.name, value: m.code })));
            return newMap;
          });
        },
        error: (err) => {
          console.error('Error loading municipalities:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile caricare la lista dei comuni'
          });
        }
      });
    }
  }



  /**
   * Load geography options (regions, provinces, municipalities) for a sub-responsible with Italy
   * Used when loading existing data from backend
   * Correct order: Country → Region → Province → Municipality
   */
  private loadGeographyOptionsForSubResponsible(
    subResponsibleId: number, 
    regionCode?: string,
    provinceCode?: string
  ): void {
    // Always load regions for Italy
    this.geographyService.getRegions('IT').subscribe({
      next: (regions) => {
        this.subResponsibleRegionsOptions.update(map => {
          const newMap = new Map(map);
          newMap.set(subResponsibleId, regions.map(r => ({ label: r.name, value: r.code })));
          return newMap;
        });
        
        // If region exists, load provinces
        if (regionCode) {
          this.geographyService.getProvinces(regionCode).subscribe({
            next: (provinces) => {
              this.subResponsibleProvincesOptions.update(map => {
                const newMap = new Map(map);
                newMap.set(subResponsibleId, provinces.map(p => ({ label: p.name, value: p.code })));
                return newMap;
              });
              
              // If province exists, load municipalities
              if (provinceCode) {
                this.geographyService.getMunicipalities(provinceCode).subscribe({
                  next: (municipalities) => {
                    this.subResponsibleMunicipalitiesOptions.update(map => {
                      const newMap = new Map(map);
                      newMap.set(subResponsibleId, municipalities.map(m => ({ label: m.name, value: m.code })));
                      return newMap;
                    });
                  },
                  error: (err) => console.error(`Error loading municipalities for sub-responsible ${subResponsibleId}:`, err)
                });
              }
            },
            error: (err) => console.error(`Error loading provinces for sub-responsible ${subResponsibleId}:`, err)
          });
        }
      },
      error: (err) => console.error(`Error loading regions for sub-responsible ${subResponsibleId}:`, err)
    });
  }

  private announceToScreenReader(message: string): void {
    this.liveRegionMessage.set(message);
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  // Validation helper methods
  private markFieldAsInvalid(fieldName: string, errorMessage: string): void {
    this.invalidFields.update(fields => {
      const newFields = new Set(fields);
      newFields.add(fieldName);
      return newFields;
    });
    this.validationErrors.update(errors => {
      const newErrors = new Map(errors);
      newErrors.set(fieldName, errorMessage);
      return newErrors;
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    return this.invalidFields().has(fieldName);
  }

  getFieldError(fieldName: string): string | undefined {
    return this.validationErrors().get(fieldName);
  }

  private clearValidationErrors(): void {
    this.invalidFields.set(new Set());
    this.validationErrors.set(new Map());
  }

  clearFieldError(fieldName: string): void {
    this.invalidFields.update(fields => {
      const newFields = new Set(fields);
      newFields.delete(fieldName);
      return newFields;
    });
    this.validationErrors.update(errors => {
      const newErrors = new Map(errors);
      newErrors.delete(fieldName);
      return newErrors;
    });
  }

  onDpoEmailChange(value: string): void {
    const normalizedValue = this.normalizeTrimmedText(value);

    if (!normalizedValue) {
      this.markFieldAsInvalid('dpoEmail', 'Mail del DPO/referente privacy è obbligatoria');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedValue)) {
      this.markFieldAsInvalid('dpoEmail', 'Inserire un indirizzo email valido');
    } else {
      this.clearFieldError('dpoEmail');
    }
  }

  validate(): boolean {
    // Clear previous validation errors
    this.clearValidationErrors();
    let isValid = true;

    // Section 1: Responsabile del Trattamento
    if (!this.hasMeaningfulText(this.responsibleCompanyName())) {
      this.markFieldAsInvalid('responsibleCompanyName', 'Ragione sociale del Responsabile del trattamento è obbligatoria');
      this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Ragione sociale del Responsabile del trattamento è obbligatoria', life: 5000 });
      isValid = false;
    }

    const dpoEmail = this.normalizeTrimmedText(this.dpoEmail());

    if (!dpoEmail) {
      this.markFieldAsInvalid('dpoEmail', 'Mail del DPO/referente privacy è obbligatoria');
      this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Mail del DPO/referente privacy è obbligatoria', life: 5000 });
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dpoEmail)) {
      this.markFieldAsInvalid('dpoEmail', 'Inserire un indirizzo email valido');
      this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Inserire un indirizzo email valido per il DPO/referente privacy', life: 5000 });
      isValid = false;
    }

    // Section 2: Dati UE/Extra UE
    if (this.dataRecipientsCategories().length === 0) {
      this.markFieldAsInvalid('dataRecipientsCategories', 'Categorie di destinatari è obbligatorio');
      isValid = false;
    }

    if (
      this.dataRecipientsCategories().includes('OTHER') &&
      !this.hasMeaningfulText(this.dataRecipientsCategoriesOther())
    ) {
      this.markFieldAsInvalid(
        'dataRecipientsCategoriesOther',
        'Specificare Altro per le categorie di destinatari è obbligatorio',
      );
      isValid = false;
    }

    if (typeof this.dataTransferOutsideEu() !== 'boolean') {
      this.markFieldAsInvalid('dataTransferOutsideEu', 'Seleziona se i dati vengono trasferiti fuori dall\'UE');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Seleziona se i dati vengono trasferiti fuori dall\'UE',
        life: 5000,
      });
      isValid = false;
    }

    if (this.dataTransferOutsideEu() === true && typeof this.gdprComplianceMeasures() !== 'boolean') {
      this.markFieldAsInvalid('gdprComplianceMeasures', 'Indica se sono state adottate misure GDPR per il trasferimento fuori UE');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Indica se sono state adottate misure GDPR per il trasferimento fuori UE',
        life: 5000,
      });
      isValid = false;
    }

    if (!this.dataDeletionMethod()) {
      this.markFieldAsInvalid('dataDeletionMethod', 'Modalità cancellazione dati è obbligatoria');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Modalità cancellazione dati è obbligatoria',
        life: 5000,
      });
      isValid = false;
    }

    if (!this.itSystemsUsed()) {
      this.markFieldAsInvalid('itSystemsUsed', 'Programmi IT/gestionale è obbligatorio');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Programmi IT/gestionale è obbligatorio',
        life: 5000,
      });
      isValid = false;
    }

    if (!this.hasMeaningfulText(this.systemAdministratorsInfo())) {
      this.markFieldAsInvalid('systemAdministratorsInfo', 'Informazioni sugli amministratori di sistema è obbligatorio');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Informazioni sugli amministratori di sistema è obbligatorio',
        life: 5000,
      });
      isValid = false;
    }

    // Check security measures checkboxes (all mandatory)
    // Note: these are boolean radio buttons (Yes/No), so both true and false are valid values
    const securityMeasures = [
      { field: 'credentialsAccess', value: this.credentialsAccess(), label: 'Credenziali di accesso' },
      { field: 'softwareUpdates', value: this.softwareUpdates(), label: 'Aggiornamenti software' },
      { field: 'passwordUpdates', value: this.passwordUpdates(), label: 'Aggiornamenti password' },
      { field: 'antivirusFirewall', value: this.antivirusFirewall(), label: 'Antivirus/Firewall' },
      { field: 'periodicBackup', value: this.periodicBackup(), label: 'Backup periodico' },
      { field: 'staffTraining', value: this.staffTraining(), label: 'Formazione personale' },
      { field: 'authorizedPersonnelList', value: this.authorizedPersonnelList(), label: 'Elenco personale autorizzato' }
    ];

    // Check that all security measures have a boolean value (not undefined/null)
    const missingSecurityMeasures = securityMeasures.filter(m => typeof m.value !== 'boolean');
    if (missingSecurityMeasures.length > 0) {
      missingSecurityMeasures.forEach(measure => {
        this.markFieldAsInvalid(measure.field, `${measure.label} è obbligatorio`);
      });
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Tutte le misure di sicurezza sono obbligatorie',
        life: 5000,
      });
      isValid = false;
    }

    // Validate sub-responsibles if present
    if (this.hasSubResponsibles()) {
      // Check if at least one sub-responsible is added
      if (this.subResponsibles().length === 0) {
        this.markFieldAsInvalid('subResponsibles', 'Aggiungi almeno un sub-responsabile o seleziona "No"');
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Aggiungi almeno un sub-responsabile o seleziona "No" per sub-responsabili presenti',
          life: 5000,
        });
        isValid = false;
      } else {
        this.subResponsibles().forEach((sub, idx) => {
          // Validate all mandatory fields
          if (!this.hasMeaningfulText(sub.companyName)) {
            this.markFieldAsInvalid(`sub-${idx}-companyName`, 'Ragione sociale è obbligatoria');
            isValid = false;
          }
          if (!sub.headquartersCountry) {
            this.markFieldAsInvalid(`sub-${idx}-headquartersCountry`, 'Nazione è obbligatoria');
            isValid = false;
          }
          if (!sub.operatingProvince) {
            this.markFieldAsInvalid(`sub-${idx}-operatingProvince`, 'Provincia è obbligatoria');
            isValid = false;
          }
          if (!sub.operatingRegion) {
            this.markFieldAsInvalid(`sub-${idx}-operatingRegion`, 'Regione è obbligatoria');
            isValid = false;
          }
          if (!sub.operatingMunicipality) {
            this.markFieldAsInvalid(`sub-${idx}-operatingMunicipality`, 'Comune è obbligatorio');
            isValid = false;
          }
          if (!sub.dataProcessingLocation) {
            this.markFieldAsInvalid(`sub-${idx}-dataProcessingLocation`, 'Luogo trattamento dati è obbligatorio');
            isValid = false;
          }
          if (!sub.specificLocationLegalBasis) {
            this.markFieldAsInvalid(`sub-${idx}-specificLocationLegalBasis`, 'Base giuridica è obbligatoria');
            isValid = false;
          }
          if (!sub.dataTypesProcessed.length) {
            this.markFieldAsInvalid(`sub-${idx}-dataTypesProcessed`, 'Tipologie di dati sono obbligatorie');
            isValid = false;
          }
          if (!this.hasMeaningfulText(sub.processesDescription)) {
            this.markFieldAsInvalid(`sub-${idx}-processesDescription`, 'Descrizione processi è obbligatoria');
            isValid = false;
          }
          if (sub.dataProcessingLocation === 'EXTRA_EU' && !sub.extraEuTransferSafeguards) {
            this.markFieldAsInvalid(`sub-${idx}-extraEuTransferSafeguards`, 'Garanzie Extra UE sono obbligatorie');
            isValid = false;
          }
        });

        if (!isValid) {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Compila tutti i campi obbligatori per i sub-responsabili',
            life: 5000,
          });
        }
      }
    }

    // Validate cloud section
    if (this.cloudStorageUsed() && !this.hasMeaningfulText(this.cloudType())) {
      this.markFieldAsInvalid('cloudType', 'Specificare il tipo di cloud');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Specificare il tipo di cloud se i dati vengono salvati sul cloud',
        life: 5000,
      });
      isValid = false;
    }

    // Scroll to first invalid field if validation failed
    if (!isValid) {
      setTimeout(() => this.scrollToFirstInvalidField(), 100);
    }

    return isValid;
  }

  private scrollToFirstInvalidField(): void {
    const firstInvalidElement = document.querySelector('.ng-invalid, .p-invalid, [aria-invalid="true"]');
    if (firstInvalidElement) {
      firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (firstInvalidElement as HTMLElement).focus?.();
    }
  }

  private _performSave(status: 'DRAFT' | 'FILLED'): void {
    const professionalId = this.getCurrentProfessionalId();
    if (!professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Professional ID non trovato',
        life: 5000,
      });
      return;
    }

    const payload = {
      responsibleCompanyName: this.responsibleCompanyName(),
      dpoFirstName: this.dpoFirstName(),
      dpoLastName: this.dpoLastName(),
      dpoEmail: this.dpoEmail(),
      // The professionals service persists this field as VARCHAR; JSON keeps
      // the multiple selected dropdown keys lossless for the contract prefill.
      dataRecipientsCategories: JSON.stringify(this.dataRecipientsCategories()),
      dataRecipientsCategoriesOther: this.dataRecipientsCategoriesOther(),
      dataTransferOutsideEu: this.dataTransferOutsideEu(),
      gdprComplianceMeasures: this.gdprComplianceMeasures(),
      dataDeletionMethod: this.dataDeletionMethod(),
      itSystemsUsed: 'SOL',
      itSystemsOther: this.itSystemsOther(),
      credentialsAccess: this.credentialsAccess(),
      softwareUpdates: this.softwareUpdates(),
      passwordUpdates: this.passwordUpdates(),
      antivirusFirewall: this.antivirusFirewall(),
      periodicBackup: this.periodicBackup(),
      staffTraining: this.staffTraining(),
      authorizedPersonnelList: this.authorizedPersonnelList(),
      hasSubResponsibles: this.hasSubResponsibles(),
      subResponsibles: this.subResponsibles().map(s => {
        // Rimuove i campi operating* (interni al FE) e li rimappa in headquarters* per il BE
        const { operatingRegion, operatingProvince, operatingMunicipality, ...rest } = s;
        return {
          ...rest,
          headquartersRegion: operatingRegion,
          headquartersProvince: operatingProvince,
          headquartersMunicipality: operatingMunicipality,
        };
      }),
      systemAdministratorsInfo: this.systemAdministratorsInfo(),
      cloudStorageUsed: this.cloudStorageUsed(),
      cloudType: this.cloudType(),
      status: status,
    };

    this.isSaving.set(true);
    
    this.professionalsService.saveVRFQuestionnaire(professionalId, payload as any).subscribe({
      next: (response) => {
        this.isSaving.set(false);
        // Reset dirty flag after successful save
        this.isDirty.set(false);
        
        // Update VRF status to reflect saved state
        this.vrfStatus.set(status);
        
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: status === 'DRAFT' 
            ? 'Bozza salvata con successo' 
            : 'Questionario salvato con successo',
          life: 3000,
        });
        
        // Navigate back to questionnaire attachments page after saving (not draft)
        if (status === 'FILLED') {
          setTimeout(() => {
            this.router.navigate(['/axa/professionals-questionnaire-attachments']);
          }, 500);
        }
      },
      error: (error) => {
        this.isSaving.set(false);
        console.error('Error saving VRF questionnaire:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Errore durante il salvataggio del questionario',
          life: 5000,
        });
      },
    });
  }

  /**
   * Navigate back to questionnaire attachments page
   */
  goBack(): void {
    this.router.navigate(['/axa/professionals-questionnaire-attachments']).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /**
   * Navigate back to progression page from read-only mode
   */
  backToProgression(): void {
    console.log('[VRF Questionnaire] Returning to progression');
    this.router.navigate(['/axa/professionals-registration-progress']).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /**
   * Get back button label based on context (review mode or normal mode)
   */
  getBackButtonLabel(): string {
    const inputId = this.professionalIdInput();
    const routeId = this.route.snapshot.paramMap.get('id');
    
    if (inputId || routeId) {
      return 'Torna a Revisione';
    }
    return 'Torna a Questionari e Allegati';
  }

  /**
   * Check if we're in review mode (professional ID passed via input or route)
   */
  isReviewMode(): boolean {
    return !!(this.professionalIdInput() || this.route.snapshot.paramMap.get('id'));
  }

  /**
   * Navigate back to questionnaire attachments page or review page
   */
  navigateBack(): void {
    // If professionalIdInput is provided, we're in review mode - go back to review page
    const inputId = this.professionalIdInput();
    const routeId = this.route.snapshot.paramMap.get('id');
    
    if (inputId || routeId) {
      console.log('[VRF Questionnaire] Returning to review page');
      const professionalId = inputId || routeId;
      this.router.navigate(['/axa/review-professional', professionalId]).then(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    } else {
      console.log('[VRF Questionnaire] Returning to questionnaire attachments');
      this.router.navigate(['/axa/professionals-questionnaire-attachments']).then(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  saveDraft(): void {
    this._performSave('DRAFT');
  }

  save(): void {
    if (!this.validate()) {
      return;
    }

    this._performSave('FILLED');
  }

  private parseRecipientCategories(value: string | string[] | null | undefined): string[] {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        .map(item => item.trim());
    }

    const normalizedValue = value.trim();
    if (!normalizedValue) {
      return [];
    }

    if (normalizedValue.startsWith('[')) {
      try {
        const parsedValue: unknown = JSON.parse(normalizedValue);
        if (Array.isArray(parsedValue)) {
          return parsedValue
            .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
            .map(item => item.trim());
        }
      } catch {
        // Fall back to comma-separated values for legacy records.
      }
    }

    return normalizedValue.split(',').map(item => item.trim()).filter(Boolean);
  }

  private normalizeRecipientCategoryKeys(values: string[]): string[] {
    const options = this.dataRecipientsCategoriesOptions();
    if (options.length === 0) {
      return values;
    }

    return values.map((value) => {
      const normalizedValue = value.trim().toLowerCase();
      const matchingOption = options.find(
        (option) =>
          option.value.toLowerCase() === normalizedValue ||
          option.label.trim().toLowerCase() === normalizedValue,
      );

      return matchingOption?.value || (options.some((option) => option.value === 'OTHER') ? 'OTHER' : value);
    });
  }

  /**
   * Validate email format
   * @param email - Email address to validate
   * @returns true if valid, false otherwise
   */
  private isValidEmail(email: string): boolean {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  /**
   * Validate Italian VAT number (Partita IVA)
   * @param vat - VAT number to validate (11 digits)
   * @returns true if valid, false otherwise
   */
  private isValidItalianVAT(vat: string): boolean {
    if (!vat) return false;
    const vatRegex = /^\d{11}$/;
    return vatRegex.test(vat.trim());
  }
}
